<template>
  <div v-if="showSection" class="row mt-4 align-items-center store-section" data-aos="fade-up" data-aos-delay="200">
    <!-- Left Column - Store Image (only if exists) -->
    <div 
      v-if="storeImage && storeImage.trim() !== ''" 
      class="col-lg-6 col-md-6 mb-4 mb-md-0" 
      data-aos="fade-right" 
      data-aos-delay="700"
    >
      <a 
        v-if="storeImageLink && storeImageLink.trim() !== ''"
        :href="storeImageLink"
        :target="storeImageLink.startsWith('http') ? '_blank' : '_self'"
        :rel="storeImageLink.startsWith('http') ? 'noopener noreferrer' : undefined"
        class="store-image-link"
      >
        <img 
          :src="storeImage" 
          :alt="storeTitle" 
          :class="['store-image', `store-image-${storeImageSize || 'medium'}`]"
          @error="handleImageError"
          @load="handleImageLoad"
        />
      </a>
      <img 
        v-else
        :src="storeImage" 
        :alt="storeTitle" 
        :class="['store-image', `store-image-${storeImageSize || 'medium'}`]"
        @error="handleImageError"
        @load="handleImageLoad"
      />
    </div>
    
    <!-- Right Column - Store Description -->
    <div 
      :class="storeImage && storeImage.trim() !== '' ? 'col-lg-6 col-md-6' : 'col-12'" 
      data-aos="fade-left" 
      data-aos-delay="800"
    >
      <div class="store-description">
        <h3 class="store-title">{{ storeTitle }}</h3>
        
        <div v-if="description1" class="store-text" v-html="formatText(description1)"></div>
        <div v-if="description2" class="store-text" v-html="formatText(description2)"></div>
        <div v-if="description3" class="store-text" v-html="formatText(description3)"></div>
        
        <ul v-if="features && features.length > 0" class="store-features">
          <li v-for="(feature, index) in features" :key="index">{{ feature }}</li>
        </ul>
        
        <p v-if="conclusion" class="store-conclusion">{{ conclusion }}</p>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
interface Props {
  showSection: boolean
  storeTitle: string
  storeImage?: string
  storeImageLink?: string
  storeImageSize?: 'tiny' | 'mini' | 'small' | 'medium' | 'large' | 'full'
  description1?: string
  description2?: string
  description3?: string
  features?: string[]
  conclusion?: string
}

defineProps<Props>()

const handleImageError = (event: Event) => {
  const target = event.target as HTMLImageElement
  target.style.display = 'none'
}

const handleImageLoad = (event: Event) => {
  const target = event.target as HTMLImageElement
  target.style.display = 'block'
  target.style.margin = '0 auto'
}

// Format text to preserve line breaks and paragraphs
const formatText = (text: string): string => {
  if (!text) return ''
  
  // Replace double line breaks with paragraph breaks
  // Replace single line breaks with <br>
  return text
    .split('\n\n')
    .map(paragraph => `<p>${paragraph.replace(/\n/g, '<br>')}</p>`)
    .join('')
}
</script>

<style scoped>
.store-section {
  margin-top: 3rem;
}

.store-image-link {
  display: block;
  text-decoration: none;
}

.store-image {
  height: auto;
  border-radius: 16px;
  transition: transform 0.3s ease, box-shadow 0.3s ease;
}

/* Size variants */
.store-image-tiny {
  max-width: 200px !important;
  width: auto;
  display: block;
  margin: 0 auto;
}

.store-image-mini {
  max-width: 300px !important;
  width: auto;
  display: block;
  margin: 0 auto;
}

.store-image-small {
  max-width: 400px !important;
  width: auto;
  display: block;
  margin: 0 auto;
}

.store-image-medium {
  max-width: 600px !important;
  width: auto;
  display: block;
  margin: 0 auto;
}

.store-image-large {
  max-width: 800px !important;
  width: auto;
  display: block;
  margin: 0 auto;
}

.store-image-full {
  width: 100%;
  max-width: 100%;
  display: block;
}

.store-image:hover,
.store-image-link:hover .store-image {
  transform: translateY(-5px);
  box-shadow: 0 15px 40px rgba(0, 0, 0, 0.2);
  cursor: pointer;
}

.store-description {
  padding: 1rem;
}

.store-title {
  font-size: 2rem;
  font-weight: 700;
  color: #2c3e50;
  margin-bottom: 1.5rem;
}

.store-text {
  font-size: 1.1rem;
  line-height: 1.8;
  color: #555;
  margin-bottom: 1.25rem;
}

.store-features {
  list-style: none;
  padding: 0;
  margin: 1.5rem 0;
}

.store-features li {
  padding: 0.75rem 0;
  padding-left: 2rem;
  position: relative;
  font-size: 1.05rem;
  color: #444;
}

.store-features li::before {
  content: '✓';
  position: absolute;
  left: 0;
  color: #0d6efd;
  font-weight: bold;
  font-size: 1.2rem;
}

.store-conclusion {
  font-size: 1.1rem;
  font-weight: 500;
  color: #2c3e50;
  margin-top: 1.5rem;
  padding: 1rem;
  background: rgba(13, 110, 253, 0.05);
  border-left: 4px solid #0d6efd;
  border-radius: 4px;
}

@media (max-width: 768px) {
  .store-title {
    font-size: 1.5rem;
  }
  
  .store-text {
    font-size: 1rem;
  }
  
  .store-features li {
    font-size: 0.95rem;
  }
}
</style>
